import { DayPicker, type ClassNames, type DayPickerProps, type Matcher, type PropsBase } from "react-day-picker";
import { enUS, fr } from "react-day-picker/locale";

import type { Dayjs } from "@eisox/dayjs";

import { cn } from "..";
import { useLocale } from "../Locale";
import {
  EMPTY_DATE_RANGE,
  fromRdpRange,
  toDisabledMatchers,
  toMonthBoundary,
  toRdpDate,
  toRdpRange,
  withDayOf,
  type DateRange,
} from "./calendar.helper";

const LOCALES = { fr, en: enUS };

// CLASS_NAMES replaces react-day-picker/style.css.
const CLASS_NAMES: Partial<ClassNames> = {
  root: "relative box-border text-base text-primary-dark-blue",
  months: "relative flex max-w-fit flex-wrap gap-8",
  month: "relative",
  month_caption: "pointer-events-none flex h-11 items-center font-medium",
  caption_label: "pointer-events-auto relative z-1 inline-flex items-center border-0 whitespace-nowrap",
  dropdowns: "pointer-events-auto relative inline-flex items-center gap-2",
  dropdown_root: "relative inline-flex items-center",
  dropdown: "absolute inset-0 z-2 w-full cursor-inherit appearance-none border-none p-0 opacity-0",
  nav: "absolute top-0 right-0 z-2 flex h-11 items-center",
  button_previous:
    "inline-flex h-9 w-9 cursor-pointer appearance-none items-center justify-center bg-transparent disabled:cursor-default disabled:opacity-50 aria-disabled:cursor-default aria-disabled:opacity-50",
  button_next:
    "inline-flex h-9 w-9 cursor-pointer appearance-none items-center justify-center bg-transparent disabled:cursor-default disabled:opacity-50 aria-disabled:cursor-default aria-disabled:opacity-50",
  chevron: "inline-block fill-primary-blue",
  month_grid: "border-collapse",
  weekday: "py-2 text-center text-sm font-medium text-primary-dark-gray",
  week_number: "h-11 w-11 text-center text-sm text-primary-dark-gray",
  day: "h-11 w-11 text-center [&:not([data-outside])][data-today]:text-primary-blue",
  day_button:
    "flex h-[42px] w-[42px] cursor-pointer items-center justify-center rounded-full border-2 border-transparent bg-transparent p-0 font-inherit text-inherit disabled:cursor-default",
  outside: "opacity-75",
  disabled: "opacity-50",
  hidden: "invisible",
};

const SINGLE_SELECTION: Partial<ClassNames> = {
  selected: "font-semibold [&>button]:bg-primary-blue [&>button]:text-primary-white",
};

// Selection unstyled in range mode so range_* classes own the look.
const RANGE_SELECTION: Partial<ClassNames> = {
  range_start:
    "rounded-l-full bg-primary-blue/8 font-semibold [&>button]:bg-primary-blue [&>button]:text-primary-white",
  range_end: "rounded-r-full bg-primary-blue/8 font-semibold [&>button]:bg-primary-blue [&>button]:text-primary-white",
  range_middle: "bg-primary-blue/8 [&>button]:rounded-none",
};

interface CalendarSharedProps extends Omit<
  PropsBase,
  "locale" | "classNames" | "disabled" | "mode" | "selected" | "onSelect" | "required"
> {
  min?: Dayjs;
  max?: Dayjs;
  disabledDates?: Matcher | Matcher[];
}

interface CalendarSingleProps extends CalendarSharedProps {
  mode?: "single";
  value?: Dayjs | null;
  onValueChange?: (value: Dayjs | null) => void;
}

interface CalendarRangeProps extends CalendarSharedProps {
  mode: "range";
  value?: DateRange;
  onValueChange?: (value: DateRange) => void;
}

type CalendarProps = CalendarSingleProps | CalendarRangeProps;

const toDayPickerProps = (props: CalendarProps, locale: (typeof LOCALES)[keyof typeof LOCALES]): DayPickerProps => {
  const isRange = props.mode === "range";
  const { min, max, disabledDates, className, mode: _, value: __, onValueChange: ___, ...rest } = props;

  const shared = {
    ...rest,
    startMonth: toMonthBoundary(min),
    endMonth: toMonthBoundary(max),
    locale,
    disabled: toDisabledMatchers(min, max, disabledDates),
    className: cn("w-fit", className),
    classNames: { ...CLASS_NAMES, ...(isRange ? RANGE_SELECTION : SINGLE_SELECTION) },
  };

  if (isRange) {
    const value = props.value ?? EMPTY_DATE_RANGE;
    return {
      ...shared,
      mode: "range",
      selected: toRdpRange(value),
      onSelect: selected => props.onValueChange?.(fromRdpRange(selected, value)),
    };
  }

  return {
    ...shared,
    mode: "single",
    selected: toRdpDate(props.value),
    onSelect: selected => props.onValueChange?.(selected ? withDayOf(props.value, selected) : null),
  };
};

const Calendar = (props: CalendarProps) => {
  const locale = LOCALES[useLocale()];
  return <DayPicker {...toDayPickerProps(props, locale)} />;
};

Calendar.displayName = "Calendar";

export { Calendar };
export type { CalendarProps, CalendarRangeProps, CalendarSharedProps, CalendarSingleProps, DateRange };
